import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";

/**
 * The turn-zero objective, acknowledged once in the thread.
 *
 * A transcript artifact: it marks the moment an objective was captured, and
 * `StudyObjectivesRow` (the builder panel) owns current truth from there on.
 * Since Wave 8 it quotes nothing, so a later edit cannot put the two out of
 * step; it only ever claims that an objective exists to go read.
 *
 * One line regardless of `source`: "Objective defined." It used to read
 * "Objectives ready to review.", which claimed a review step that does not
 * exist here: at turn zero nothing has been produced yet, the marketer has
 * simply said what the study is for.
 *
 * Phase independence is a requirement of this line, not a nicety. The
 * component reads live `studyGoals`, so wording that is only true before the
 * planner restructures the objective would silently rewrite its own meaning
 * in the transcript afterwards. "Objective defined." holds at every later
 * phase.
 *
 * Earlier the line branched on whether the words were the marketer's
 * (`confirmed`, so echoing them back would read as a duplicate) or ours
 * (`inferred`, so we showed the decision text since they had not seen it yet).
 * That branch existed only because nothing else on screen held the objective.
 * As of Wave 8 the builder panel shows the objective immediately, pre-draft,
 * so this receipt no longer needs to carry the text at all. `source` stays on
 * the props (call sites still pass it) but no longer affects what renders.
 *
 * Follows `ConfirmedReceipt` (QuickStudyPage) as the platform's one
 * start-of-study receipt shape: green check, regular full-strength text, no
 * bubble, no avatar, no dim. Everything else per the objectives-row design
 * spec (docs/design-briefs/objectives-row): no card chrome, no labels, no
 * uppercase, no letter-spacing.
 */
export function ObjectiveReceipt(props: {
  decision: string | null | undefined;
  source?: "inferred" | "confirmed" | "edited";
  className?: string;
}) {
  const { t } = useTranslation("app");
  const decision = (props.decision ?? "").trim();

  if (!decision) {
    return null;
  }

  return (
    <div
      className={cn(
        // pl-[36px] matches the avatar (h-7) + gap-2 column the assistant's
        // own bubble text starts at, the same indent this file already uses
        // for its other chrome-less system lines (chat-preparing-indicator,
        // the slow-response warning).
        "animate-fade-in flex items-start gap-1.5 pl-[36px] motion-reduce:animate-none",
        props.className,
      )}
      data-testid="questionnaire-objective-receipt"
    >
      <Check
        className="mt-[3px] h-4 w-4 shrink-0 text-[hsl(var(--chart-positive))]"
        aria-hidden="true"
      />
      <Text as="p" className="text-sm font-medium leading-[1.6] text-foreground">
        {t("questionnaire.objectiveDefined")}
      </Text>
    </div>
  );
}
